/**
 * JWT Token Utilities
 * Handles token decoding, validation, and expiration checking
 */

/**
 * Decode a JWT token without verification
 * @param {string} token - JWT token
 * @returns {object|null} Decoded token payload or null if invalid
 */
export const decodeToken = (token) => {
  if (!token) return null;

  try {
    const base64Url = token.split(".")[1];
    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Failed to decode token:", error);
    return null;
  }
};

/**
 * Check if a token is expired
 * @param {string} token - JWT token
 * @param {number} bufferSeconds - Buffer time in seconds before actual expiration (default: 60)
 * @returns {boolean} True if token is expired or will expire within buffer time
 */
export const isTokenExpired = (token, bufferSeconds = 60) => {
  const payload = decodeToken(token);
  if (!payload || !payload.exp) return true;

  const expirationTime = payload.exp * 1000; // Convert to milliseconds
  const currentTime = Date.now();
  const bufferTime = bufferSeconds * 1000;

  return currentTime >= expirationTime - bufferTime;
};

/**
 * Get token expiration time
 * @param {string} token - JWT token
 * @returns {Date|null} Expiration date or null if invalid
 */
export const getTokenExpiration = (token) => {
  const payload = decodeToken(token);
  if (!payload || !payload.exp) return null;

  return new Date(payload.exp * 1000);
};

/**
 * Get time until token expires in seconds
 * @param {string} token - JWT token
 * @returns {number} Seconds until expiration, or 0 if expired/invalid
 */
export const getTimeUntilExpiration = (token) => {
  const payload = decodeToken(token);
  if (!payload || !payload.exp) return 0;

  const expirationTime = payload.exp * 1000;
  const currentTime = Date.now();
  const timeRemaining = Math.max(0, Math.floor((expirationTime - currentTime) / 1000));

  return timeRemaining;
};

/**
 * Extract user information from token
 * @param {string} token - JWT token
 * @returns {object|null} User info (sub, email, etc.) or null if invalid
 */
export const getUserFromToken = (token) => {
  const payload = decodeToken(token);
  if (!payload) return null;

  return {
    userId: payload.sub,
    email: payload.email,
    username: payload["cognito:username"],
    groups: payload["cognito:groups"] || [],
  };
};
